import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import { useTranslation } from "react-i18next";
import {
  deleteSkillAgentRapport,
  fetchSkillAgentRapportBlob,
  generateSkillAgentRapportTile,
  getSkillAgentRapportById,
  updateSkillAgentRapport,
} from "../../api/Skills/skillAgentRapportsApi";
import { pageFieldSx } from "../../styles/pageModernStyles";
import SkillAgentInlineTitle from "./SkillAgentInlineTitle";
import {
  boundingRectFromCorners,
  cornersFromRect,
  deskewRadiansFromRule,
  motifHalfDropSeamless,
  photoHasLightGround,
  hitQuadCorner,
  isRuleSkewed,
  makeSeamless,
  mapCornersAfterRotation,
  mapRectAfterRotation,
  mapRuleAfterRotation,
  normalizeRect,
  parseCorners,
  parseMarks,
  popRapportHistory,
  pushRapportHistory,
  quadSizePx,
  rapportCmFromMarks,
  rapportDrivePathLabel,
  rotatedCanvasSize,
  forceWrapEdges,
  rapportIntent,
  rapportToolKind,
  tilePxFromRapportCm,
  warpFromScaleLine,
  warpQuadToRgba,
} from "./skillAgentRapport";
import { SkillAgentRapportIntentDialog } from "./SkillAgentRapportPickDialog";
import SkillConfirmDialog from "./SkillConfirmDialog";

const MAX_EDGE = 2000;

function RapportToolGroup({ label, accent = false, muted = false, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.15,
        px: 0.5,
        py: 0.15,
        borderRadius: 1.5,
        opacity: muted ? 0.42 : 1,
        bgcolor: (t) =>
          accent
            ? alpha(t.palette.primary.main, 0.08)
            : alpha(t.palette.text.primary, 0.03),
        border: "1px solid",
        borderColor: (t) =>
          accent ? alpha(t.palette.primary.main, 0.28) : "divider",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          px: 0.65,
          fontWeight: 800,
          fontSize: "0.62rem",
          letterSpacing: 0.04,
          color: accent ? "primary.main" : "text.secondary",
          textTransform: "uppercase",
          userSelect: "none",
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function hitRepeat(rect, x, y, handle) {
  if (!rect || rect.w < 8 || rect.h < 8) return null;
  const hx = rect.x + rect.w;
  const hy = rect.y + rect.h;
  if (Math.abs(x - hx) <= handle && Math.abs(y - hy) <= handle) return "resize";
  if (x >= rect.x && x <= hx && y >= rect.y && y <= hy) return "move";
  return null;
}

async function downscaleFile(file) {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
  bmp.close?.();
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.92),
  );
  return blob;
}

function bitmapSize(img) {
  return {
    sw: img.naturalWidth || img.width,
    sh: img.naturalHeight || img.height,
  };
}

/** Escala un tile periódico. Sin relleno crema: en animal print se ve como rejilla. */
function drawImageSeamless(ctx, img, destW, destH) {
  const { sw, sh } = bitmapSize(img);
  if (!(sw > 0) || !(sh > 0) || !(destW > 0) || !(destH > 0)) return;
  const pad = Math.min(
    Math.floor(Math.min(sw, sh) / 4),
    Math.max(8, Math.ceil((Math.max(sw / destW, sh / destH) || 1) * 2)),
  );
  const wrapped = document.createElement("canvas");
  wrapped.width = sw + pad * 2;
  wrapped.height = sh + pad * 2;
  const wctx = wrapped.getContext("2d");
  const blit = (sx, sy, sW, sH, dx, dy) => {
    wctx.drawImage(img, sx, sy, sW, sH, dx, dy, sW, sH);
  };
  blit(0, 0, sw, sh, pad, pad);
  blit(Math.max(0, sw - pad), 0, pad, sh, 0, pad);
  blit(0, 0, pad, sh, sw + pad, pad);
  blit(0, Math.max(0, sh - pad), sw, pad, pad, 0);
  blit(0, 0, sw, pad, pad, sh + pad);
  blit(Math.max(0, sw - pad), Math.max(0, sh - pad), pad, pad, 0, 0);
  blit(0, Math.max(0, sh - pad), pad, pad, sw + pad, 0);
  blit(Math.max(0, sw - pad), 0, pad, pad, 0, sh + pad);
  blit(0, 0, pad, pad, sw + pad, sh + pad);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  const sx = destW / sw;
  const sy = destH / sh;
  // +1 px: evita huecos subpíxel que dejan ver el fondo y parecen junta.
  ctx.drawImage(
    wrapped,
    -pad * sx - 0.5,
    -pad * sy - 0.5,
    (sw + pad * 2) * sx + 1,
    (sh + pad * 2) * sy + 1,
  );
}

/**
 * Solo garantiza L=R / T=B. No vuelve a quiltear:
 * un min-cut gordo encima del del AI deja 2–3 rayas fantasma en cada junta.
 */
async function ensureSeamlessBlob(blob) {
  if (!blob) return blob;
  try {
    const bmp = await createImageBitmap(blob);
    const w = bmp.width;
    const h = bmp.height;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(bmp, 0, 0);
    bmp.close?.();
    if (w > 32 && h > 32) {
      const image = ctx.getImageData(0, 0, w, h);
      const data = forceWrapEdges(image.data, w, h);
      ctx.putImageData(new ImageData(data, w, h), 0, 0);
    }
    const next = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/png"),
    );
    return next || blob;
  } catch {
    return blob;
  }
}

async function sealTileBlob(blob) {
  return ensureSeamlessBlob(blob);
}

async function resizeImageBlob(blob, width, height) {
  if (!blob || !(width > 0) || !(height > 0)) return blob;
  const bmp = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  drawImageSeamless(canvas.getContext("2d"), bmp, width, height);
  bmp.close?.();
  const resized = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/png"),
  );
  return ensureSeamlessBlob(resized || blob);
}

async function rotateImageToBlob(img, radians) {
  const srcW = img.naturalWidth;
  const srcH = img.naturalHeight;
  const size = rotatedCanvasSize(srcW, srcH, radians);
  if (!size) return null;
  const canvas = document.createElement("canvas");
  canvas.width = size.w;
  canvas.height = size.h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size.w, size.h);
  ctx.translate(size.w / 2, size.h / 2);
  ctx.rotate(radians);
  ctx.drawImage(img, -srcW / 2, -srcH / 2);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
}

const INTERPRET = "#6d28d9";

function StepMark({ n, color = INTERPRET }) {
  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        bgcolor: alpha(color, 0.12),
        color,
        display: "grid",
        placeItems: "center",
        fontSize: "0.72rem",
        fontWeight: 800,
        flexShrink: 0,
      }}
    >
      {n}
    </Box>
  );
}

function interpretCardSx(theme, accent = INTERPRET) {
  return {
    borderRadius: 3,
    overflow: "hidden",
    border: "1px solid",
    borderColor: alpha(accent, 0.14),
    bgcolor: theme.palette.background.paper,
    boxShadow: "0 10px 28px rgba(80, 60, 40, 0.05)",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    height: "100%",
  };
}

function downloadBlobUrl(url, filename) {
  if (!url) return;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
}

function InterpretLayout({
  t,
  theme,
  imageUrl,
  tileUrl,
  fileDrag,
  tool,
  setTool,
  rule,
  scaleCm,
  setScaleCm,
  setTileStale,
  measured,
  generating,
  saving,
  canUndo,
  canRedo,
  undoMarksRef,
  redoMarksRef,
  fileRef,
  imgRef,
  canvasRef,
  tilePreviewRef,
  setImgSize,
  imgSize,
  onFileDragOver,
  onFileDragLeave,
  onFileDrop,
  onPickFile,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onStraighten,
  onInterpretClick,
  ancho,
  setAncho,
  rapportW,
  setRapportW,
  rapportH,
  setRapportH,
  direccion,
  setDireccion,
  setDirectionTouched,
  titulo,
  setTitulo,
  onTitleCommit,
  downloadSvg,
  canDownloadSvg = false,
  onDownloadTilePng = null,
  branch,
  onSave,
  onDelete,
  row,
  drivePath,
  error,
  savedNote,
  isTile = false,
  intent = null,
  onGenerateClick,
  tileStale = false,
  textileMode = true,
  weave = "auto",
  setWeave,
  resultSvgUrl = "",
  seamlessPreviewUrl = "",
  runHistory = [],
  onSelectHistory,
  onDownloadHistorySvg,
  onDownloadHistoryPng,
  lastRunStats = null,
}) {
  const isInvent = !isTile && intent === "invent";
  const accent = isTile ? "#a16207" : isInvent ? "#0f766e" : INTERPRET;
  const card = interpretCardSx(theme, accent);
  const toolName = isTile ? "Tartán" : "Seamless";
  const widthVal = Number(rapportW) || Number(ancho) || 150;
  const heightVal = Number(rapportH) || 150;
  const stageRef = useRef(null);
  const resultStageRef = useRef(null);
  const rowRef = useRef(null);
  const splitRef = useRef(null);
  const colSplitRef = useRef(null);
  const zoomRef = useRef(1);
  const [zoom, setZoom] = useState(1);
  const [stageBox, setStageBox] = useState({ w: 0, h: 0 });
  const [resultBox, setResultBox] = useState({ w: 0, h: 0 });
  const [photoFrac, setPhotoFrac] = useState(0.56);
  const [workHeight, setWorkHeight] = useState(() =>
    typeof window === "undefined"
      ? 560
      : Math.round(
          Math.min(820, window.innerHeight * (textileMode ? 0.68 : 0.58)),
        ),
  );
  zoomRef.current = zoom;

  useEffect(() => {
    const photoEl = stageRef.current;
    const resultEl = resultStageRef.current;
    const measure = () => {
      if (photoEl) {
        setStageBox({ w: photoEl.clientWidth, h: photoEl.clientHeight });
      }
      if (resultEl) {
        setResultBox({ w: resultEl.clientWidth, h: resultEl.clientHeight });
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (photoEl) observer.observe(photoEl);
    if (resultEl) observer.observe(resultEl);
    const onWheel = (event) => {
      if (!event.altKey) return;
      if (!imageUrl && !tileUrl) return;
      event.preventDefault();
      const prev = zoomRef.current;
      const next = Math.min(
        4,
        Math.max(
          1,
          Math.round((prev + (event.deltaY < 0 ? 0.2 : -0.2)) * 100) / 100,
        ),
      );
      if (next === prev) return;
      const ratio = next / prev;
      const origin = event.currentTarget;
      setZoom(next);
      requestAnimationFrame(() => {
        [photoEl, resultEl].forEach((el) => {
          if (!el) return;
          const rect = el.getBoundingClientRect();
          const focusX =
            el === origin ? event.clientX : rect.left + rect.width / 2;
          const focusY =
            el === origin ? event.clientY : rect.top + rect.height / 2;
          const ox = focusX - rect.left + el.scrollLeft;
          const oy = focusY - rect.top + el.scrollTop;
          el.scrollLeft = ox * ratio - (focusX - rect.left);
          el.scrollTop = oy * ratio - (focusY - rect.top);
        });
      });
    };
    photoEl?.addEventListener("wheel", onWheel, { passive: false });
    resultEl?.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      observer.disconnect();
      photoEl?.removeEventListener("wheel", onWheel);
      resultEl?.removeEventListener("wheel", onWheel);
    };
  }, [imageUrl, tileUrl]);

  const frame = (() => {
    if (
      !imageUrl ||
      !imgSize?.w ||
      !imgSize?.h ||
      stageBox.w < 40 ||
      stageBox.h < 40
    ) {
      return null;
    }
    const contain = Math.min(
      (stageBox.w - 16) / imgSize.w,
      (stageBox.h - 16) / imgSize.h,
    );
    return {
      w: Math.max(40, imgSize.w * contain * zoom),
      h: Math.max(40, imgSize.h * contain * zoom),
    };
  })();

  const resultFrame =
    resultBox.w > 40 && resultBox.h > 40
      ? {
          w: Math.max(40, resultBox.w * zoom),
          h: Math.max(40, resultBox.h * zoom),
        }
      : null;
  const toggleSx = {
    bgcolor: "transparent",
    borderRadius: 2,
    p: 0.3,
    gap: 0.25,
    "& .MuiToggleButton-root": {
      textTransform: "none",
      fontWeight: 700,
      fontSize: "0.78rem",
      px: 1.25,
      py: 0.45,
      border: 0,
      borderRadius: 1.5,
      color: "text.secondary",
    },
    "& .Mui-selected": {
      bgcolor: `${theme.palette.background.paper} !important`,
      color: accent,
      boxShadow: "0 1px 2px rgba(20,16,12,0.08)",
    },
  };

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ minWidth: 0 }}
      >
        <Box sx={{ flex: "0 1 220px", minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: accent,
              fontWeight: 700,
              letterSpacing: 0.04,
              textTransform: "uppercase",
              fontSize: "0.65rem",
            }}
          >
            {[
              branch,
              t(
                isTile
                  ? "skillAgent.tree.folderRapportTiles"
                  : "skillAgent.tree.folderRapportInterpret",
                {
                  defaultValue: isTile ? "TARTÁN" : "SEAMLESS",
                },
              ),
            ]
              .filter(Boolean)
              .join(" · ") || t("skillAgent.tree.folderRapports")}
          </Typography>
          <SkillAgentInlineTitle
            entityKey={row?.id}
            value={titulo}
            onChange={setTitulo}
            onCommit={onTitleCommit}
            placeholder={t("skillAgent.form.title")}
            disabled={saving || generating}
            sx={{ maxWidth: "100%", width: "100%" }}
            inputSx={{
              fontSize: "1.35rem",
              letterSpacing: "-0.03em",
            }}
          />
        </Box>
        {error || savedNote ? (
          <Alert
            severity={error ? "error" : "success"}
            sx={{
              flex: "1 1 160px",
              minWidth: 0,
              py: 0.25,
              px: 1,
              alignItems: "center",
              "& .MuiAlert-message": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                py: 0,
                fontSize: "0.8rem",
              },
              "& .MuiAlert-icon": { py: 0, mr: 0.75 },
            }}
          >
            {error || savedNote}
          </Alert>
        ) : (
          <Box sx={{ flex: "1 1 auto", minWidth: 8 }} />
        )}
        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          flexWrap="wrap"
          justifyContent="flex-end"
          alignItems="center"
          sx={{ flex: "0 0 auto" }}
        >
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={!imageUrl || generating || saving}
            onClick={isTile ? onGenerateClick : onInterpretClick}
            startIcon={
              generating ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <CropFreeOutlinedIcon fontSize="small" />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 999,
              px: 1.75,
              height: 34,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {generating
              ? t(
                  isTile
                    ? "skillAgent.rapport.generatingTartan"
                    : "skillAgent.rapport.generatingSeamless",
                  {
                    defaultValue: isTile
                      ? "Generando tartán…"
                      : "Generando seamless…",
                  },
                )
              : t("skillAgent.rapport.generate", {
                  defaultValue: "Generar",
                })}
          </Button>
          {!textileMode ? (
            <RapportToolGroup label={t("skillAgent.rapport.groupHistory")}>
              <Tooltip title={t("skillAgent.rapport.undo")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!canUndo}
                    onClick={() => undoMarksRef.current()}
                  >
                    <UndoOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t("skillAgent.rapport.redo")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!canRedo}
                    onClick={() => redoMarksRef.current()}
                  >
                    <RedoOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </RapportToolGroup>
          ) : null}
          <RapportToolGroup label={t("skillAgent.rapport.fileGroup")}>
            <Tooltip
              title={
                row?.DriveFolderUrl
                  ? t("skillAgent.rapport.openDrive")
                  : `${drivePath} — ${t("skillAgent.rapport.drivePending")}`
              }
            >
              <span>
                <IconButton
                  size="small"
                  component={row?.DriveFolderUrl ? "a" : "button"}
                  href={row?.DriveFolderUrl || undefined}
                  target={row?.DriveFolderUrl ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  disabled={!row?.DriveFolderUrl}
                >
                  <FolderOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                saving
                  ? t("skillAgent.common.saving")
                  : t("skillAgent.rapport.save")
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={saving || generating}
                  onClick={onSave}
                >
                  {saving ? (
                    <CircularProgress size={16} />
                  ) : (
                    <SaveOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("skillAgent.common.delete")}>
              <IconButton
                size="small"
                onClick={onDelete}
                sx={{ color: "text.disabled" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </RapportToolGroup>
        </Stack>
      </Stack>

      {textileMode ? (
        <Stack spacing={1}>
          <Box
            sx={{
              ...card,
              height: "auto",
              px: 1.75,
              py: 1.35,
            }}
          >
            <Typography
              fontWeight={800}
              sx={{
                fontSize: "0.78rem",
                letterSpacing: 0.04,
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              {t("skillAgent.rapport.textileOptionsTitle", {
                defaultValue: `2 · Opciones · ${toolName}`,
                tool: toolName,
              })}
            </Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ md: "center" }}
            >
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                >
                  <Typography variant="caption" fontWeight={700}>
                    {t("skillAgent.rapport.widthCmShort", {
                      defaultValue: "Ancho (cm)",
                    })}
                  </Typography>
                  <Typography variant="caption" fontWeight={800}>
                    {widthVal}
                  </Typography>
                </Stack>
                <Box
                  component="input"
                  type="range"
                  min={20}
                  max={300}
                  step={1}
                  value={widthVal}
                  onChange={(e) => {
                    const v = String(e.target.value);
                    setRapportW(v);
                    if (!ancho) setAncho(v);
                  }}
                  sx={{
                    width: "100%",
                    accentColor: accent,
                    cursor: "pointer",
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                >
                  <Typography variant="caption" fontWeight={700}>
                    {t("skillAgent.rapport.heightCmShort", {
                      defaultValue: "Alto (cm)",
                    })}
                  </Typography>
                  <Typography variant="caption" fontWeight={800}>
                    {heightVal}
                  </Typography>
                </Stack>
                <Box
                  component="input"
                  type="range"
                  min={20}
                  max={300}
                  step={1}
                  value={heightVal}
                  onChange={(e) => setRapportH(String(e.target.value))}
                  sx={{
                    width: "100%",
                    accentColor: accent,
                    cursor: "pointer",
                  }}
                />
              </Box>
              {isTile ? (
                <TextField
                  select
                  size="small"
                  label={t("skillAgent.rapport.weave", {
                    defaultValue: "Ligamento",
                  })}
                  value={weave || "auto"}
                  onChange={(e) => setWeave?.(e.target.value)}
                  sx={[pageFieldSx, { minWidth: 190, flex: "0 0 auto" }]}
                >
                  <MenuItem value="auto">
                    {t("skillAgent.rapport.weaveAuto", {
                      defaultValue: "Auto (liso si 2 tintas)",
                    })}
                  </MenuItem>
                  <MenuItem value="plain">
                    {t("skillAgent.rapport.weavePlain", {
                      defaultValue: "Liso / vichy",
                    })}
                  </MenuItem>
                  <MenuItem value="twill">
                    {t("skillAgent.rapport.weaveTwill", {
                      defaultValue: "Sarga (twill)",
                    })}
                  </MenuItem>
                </TextField>
              ) : null}
            </Stack>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(3, minmax(0, 1fr))",
                md: "repeat(6, minmax(0, 1fr))",
              },
              gap: 0.75,
            }}
          >
            {[
              {
                label: "Herramienta",
                value: lastRunStats?.engineLabel || toolName,
              },
              {
                label: "Versión",
                value: lastRunStats?.version || "—",
              },
              {
                label: "Tiempo",
                value:
                  lastRunStats?.elapsedMs != null
                    ? `${lastRunStats.elapsedMs} ms`
                    : "—",
              },
              {
                label: "Peso",
                value: lastRunStats?.svgBytes
                  ? lastRunStats.svgBytes < 1024
                    ? `${lastRunStats.svgBytes} B`
                    : `${(lastRunStats.svgBytes / 1024).toFixed(1)} KB`
                  : "—",
              },
              {
                label: "Trazos",
                value:
                  lastRunStats?.pathCount != null
                    ? String(lastRunStats.pathCount)
                    : "—",
              },
              {
                label: "cm",
                value:
                  lastRunStats?.widthCm != null
                    ? `${lastRunStats.widthCm}×${lastRunStats.heightCm ?? "—"}`
                    : "—",
              },
            ].map((metric) => (
              <Box
                key={metric.label}
                sx={{
                  px: 1.1,
                  py: 0.85,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "#fffdf9",
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.2 }}
                >
                  {metric.label}
                </Typography>
                <Typography
                  fontWeight={800}
                  sx={{
                    fontSize: "0.92rem",
                    letterSpacing: "-0.02em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {metric.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Stack>
      ) : null}

      <Box
        ref={rowRef}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          height: workHeight,
          minHeight: textileMode ? 360 : 280,
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            ...card,
            width: { xs: "100%", md: `calc(${photoFrac} * (100% - 14px))` },
            flex: { xs: 1, md: "0 0 auto" },
            minWidth: 0,
            minHeight: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ px: 1.75, pt: 1.5, pb: 1.15 }}
          >
            <StepMark n="1" color={accent} />
            <Typography fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
              {t("skillAgent.rapport.interpretPhoto")}
            </Typography>
          </Stack>

          <Box
            ref={stageRef}
            onDragEnter={onFileDragOver}
            onDragOver={onFileDragOver}
            onDragLeave={onFileDragLeave}
            onDrop={onFileDrop}
            sx={{
              mx: 1.5,
              borderRadius: 2.5,
              bgcolor: "#f3efe8",
              flex: 1,
              minHeight: 0,
              position: "relative",
              overflow: "auto",
              scrollbarGutter: "stable",
              display: imageUrl ? "block" : "grid",
              placeItems: imageUrl ? "unset" : "center",
              outline: fileDrag ? "2px dashed" : "none",
              outlineColor: accent,
              outlineOffset: -6,
            }}
          >
            {imageUrl ? (
              <Box
                sx={{
                  minWidth: "100%",
                  minHeight: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    lineHeight: 0,
                    flex: "0 0 auto",
                    width: frame ? frame.w : "100%",
                    height: frame ? frame.h : "auto",
                  }}
                >
                  <Box
                    component="img"
                    src={imageUrl}
                    alt=""
                    draggable={false}
                    onLoad={(e) => {
                      imgRef.current = e.currentTarget;
                      setImgSize({
                        w: e.currentTarget.naturalWidth,
                        h: e.currentTarget.naturalHeight,
                      });
                    }}
                    sx={{
                      display: "block",
                      width: "100%",
                      height: frame ? "100%" : "auto",
                      maxHeight: frame ? "none" : "100%",
                    }}
                  />
                  <canvas
                    ref={canvasRef}
                    onPointerDown={textileMode ? undefined : onPointerDown}
                    onPointerMove={textileMode ? undefined : onPointerMove}
                    onPointerUp={textileMode ? undefined : onPointerUp}
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      width: "100%",
                      height: "100%",
                      touchAction: textileMode ? "auto" : "none",
                      cursor: textileMode ? "default" : "crosshair",
                      pointerEvents: textileMode ? "none" : "auto",
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                onClick={() => fileRef.current?.click()}
                sx={{
                  width: "min(100% - 48px, 440px)",
                  minHeight: 220,
                  borderRadius: 2.5,
                  border: "1.5px dashed",
                  borderColor: alpha(accent, 0.4),
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  px: 3,
                  textAlign: "center",
                  bgcolor: fileDrag ? alpha(accent, 0.06) : "transparent",
                }}
              >
                <Typography fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
                  {t("skillAgent.rapport.dropUpload")}
                </Typography>
              </Box>
            )}
            {fileDrag ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 3,
                  display: "grid",
                  placeItems: "center",
                  pointerEvents: "none",
                  bgcolor: alpha(accent, 0.12),
                }}
              >
                <Typography fontWeight={800}>
                  {t("skillAgent.rapport.dropUpload")}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{
              mx: 1.5,
              mt: 1.15,
              mb: imageUrl ? 0 : 1.5,
              px: 0.75,
              py: 0.45,
              borderRadius: 2,
              bgcolor: "#f6f3ee",
            }}
          >
            <Button
              size="small"
              onClick={() => fileRef.current?.click()}
              sx={{
                fontWeight: 700,
                color: "text.secondary",
                textTransform: "none",
              }}
            >
              {imageUrl
                ? t("skillAgent.rapport.interpretChange")
                : t("skillAgent.rapport.upload")}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onPickFile}
            />
            {!textileMode && imageUrl ? (
              <>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={tool}
                  onChange={(e, value) => value && setTool(value)}
                  sx={toggleSx}
                >
                  <ToggleButton value="rule">
                    {t("skillAgent.rapport.toolRule")}
                  </ToggleButton>
                  <ToggleButton value="repeat">
                    {t(
                      isTile
                        ? "skillAgent.rapport.toolRepeat"
                        : "skillAgent.rapport.cropSwatch",
                    )}
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  size="small"
                  disabled={!isRuleSkewed(rule)}
                  onClick={onStraighten}
                  sx={{ fontWeight: 700, textTransform: "none" }}
                >
                  {t("skillAgent.rapport.straighten")}
                </Button>
              </>
            ) : null}
          </Stack>

          {!textileMode && imageUrl ? (
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ px: 1.75, pt: 0.75, pb: tool === "repeat" ? 0.4 : 1.6 }}
            >
              <TextField
                size="small"
                type="number"
                label={t("skillAgent.rapport.scaleCm")}
                value={scaleCm}
                onChange={(e) => {
                  setScaleCm(e.target.value);
                  setTileStale(true);
                }}
                sx={[pageFieldSx, { width: 132 }]}
                inputProps={{ min: 0.1, step: 0.1 }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.4 }}
              >
                {measured.pxPerCm
                  ? t("skillAgent.rapport.scaleReadout", {
                      px: Math.round(measured.pxPerCm),
                    })
                  : t(
                      isTile
                        ? "skillAgent.rapport.tileScaleMissing"
                        : "skillAgent.rapport.interpretScaleMissing",
                    )}
              </Typography>
              {isRuleSkewed(rule) ? (
                <Typography variant="body2" color="warning.main">
                  {t("skillAgent.rapport.skewWarn")}
                </Typography>
              ) : null}
            </Stack>
          ) : null}

          {!textileMode && imageUrl && tool === "repeat" ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 1.75, pt: 0, pb: 1.5, lineHeight: 1.45 }}
            >
              {t(
                isTile
                  ? "skillAgent.rapport.stretchHelp"
                  : isInvent
                    ? "skillAgent.rapport.inventCropHint"
                    : "skillAgent.rapport.clothCropHint",
              )}
            </Typography>
          ) : null}

          {textileMode && imageUrl ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 1.75, pt: 0.75, pb: 1.5, lineHeight: 1.45 }}
            >
              {t(
                isTile
                  ? "skillAgent.rapport.textilePhotoHintTartan"
                  : "skillAgent.rapport.textilePhotoHintSeamless",
                {
                  defaultValue: isTile
                    ? "Muestra plana, de frente y sin arrugas. Si no se lee el cuadriculado, te pedirá volver a fotografiar. Ancho/alto y ligamento arriba; luego Generar."
                    : "Print, leopard, floral o camo: IA tileable con fidelidad a la foto (~40 s). Ancho/alto; luego Generar.",
                },
              )}
            </Typography>
          ) : null}
        </Box>

        <Box
          ref={colSplitRef}
          onPointerDown={(event) => {
            const grip = colSplitRef.current;
            const row = rowRef.current;
            if (!grip || !row) return;
            grip.setPointerCapture(event.pointerId);
            grip.dataset.x = String(event.clientX);
            grip.dataset.f = String(photoFrac);
            grip.dataset.w = String(row.clientWidth);
          }}
          onPointerMove={(event) => {
            const grip = colSplitRef.current;
            if (!grip?.hasPointerCapture(event.pointerId)) return;
            const next =
              Number(grip.dataset.f) +
              (event.clientX - Number(grip.dataset.x)) /
                Math.max(1, Number(grip.dataset.w));
            setPhotoFrac(Math.min(0.74, Math.max(0.26, next)));
          }}
          onDoubleClick={() => setPhotoFrac(0.56)}
          sx={{
            display: { xs: "none", md: "grid" },
            width: 14,
            flex: "0 0 auto",
            cursor: "col-resize",
            placeItems: "center",
            touchAction: "none",
            "&:hover .grip, &:active .grip": { bgcolor: accent, height: 88 },
          }}
        >
          <Box
            className="grip"
            sx={{
              width: 4,
              height: 64,
              borderRadius: 99,
              bgcolor: alpha(accent, 0.35),
              transition: "height 0.15s ease, background-color 0.15s ease",
            }}
          />
        </Box>

        <Box sx={{ ...card, flex: 1, minWidth: 0, minHeight: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ px: 1.75, pt: 1.35, pb: 1 }}
          >
            <StepMark n="2" color={accent} />
            <Typography fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
              {t("skillAgent.rapport.interpretResult")}
            </Typography>
          </Stack>

          <Box
            ref={resultStageRef}
            sx={{
              mx: 1.5,
              mb: 0.5,
              borderRadius: 2.5,
              flex: 1,
              minHeight: 0,
              position: "relative",
              overflow: "auto",
              scrollbarGutter: "stable",
              bgcolor:
                tileUrl || resultSvgUrl || seamlessPreviewUrl
                  ? "#faf7f2"
                  : "#f3efe8",
            }}
          >
            {resultSvgUrl || seamlessPreviewUrl || tileUrl ? (
              <Box
                sx={{
                  minWidth: "100%",
                  minHeight: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1,
                }}
              >
                {seamlessPreviewUrl ? (
                  <Box
                    component="img"
                    src={seamlessPreviewUrl}
                    alt=""
                    sx={{
                      display: "block",
                      width: resultFrame ? resultFrame.w : "100%",
                      height: resultFrame ? resultFrame.h : "auto",
                      maxHeight: "100%",
                      objectFit: "contain",
                      bgcolor: "#fff",
                      borderRadius: 1,
                    }}
                  />
                ) : resultSvgUrl ? (
                  <Box
                    component="img"
                    src={resultSvgUrl}
                    alt=""
                    sx={{
                      display: "block",
                      width: resultFrame ? resultFrame.w : "100%",
                      height: resultFrame ? resultFrame.h : "auto",
                      objectFit: "contain",
                      bgcolor: "#fff",
                      borderRadius: 1,
                    }}
                  />
                ) : (
                  <canvas
                    ref={tilePreviewRef}
                    style={{
                      display: "block",
                      width: resultFrame ? resultFrame.w : "100%",
                      height: resultFrame ? resultFrame.h : "100%",
                    }}
                  />
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                  px: 2.5,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ maxWidth: 320, lineHeight: 1.5 }}
                >
                  {t(
                    textileMode
                      ? "skillAgent.rapport.textileGenerateHelp"
                      : isTile
                        ? "skillAgent.rapport.stretchHelp"
                        : "skillAgent.rapport.generateHelp",
                    {
                      defaultValue: textileMode
                        ? "Pulsa Generar cuando la foto esté lista."
                        : undefined,
                    },
                  )}
                </Typography>
              </Box>
            )}
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            useFlexGap
            sx={{ px: 1, pb: 1.25, pt: 0.5 }}
          >
            <Button
              size="small"
              disabled={!canDownloadSvg || !downloadSvg}
              onClick={downloadSvg}
              sx={{ fontWeight: 700, textTransform: "none" }}
            >
              {t("skillAgent.rapport.downloadSvg", {
                defaultValue: "Descargar SVG",
              })}
            </Button>
            <Button
              size="small"
              disabled={!tileUrl && !seamlessPreviewUrl}
              onClick={() => {
                if (onDownloadTilePng) onDownloadTilePng();
                else
                  downloadBlobUrl(tileUrl, `${titulo || "rapport"}-tile.png`);
              }}
              sx={{ fontWeight: 700, textTransform: "none" }}
            >
              {t("skillAgent.rapport.downloadPng", {
                defaultValue: "Descargar PNG",
              })}
            </Button>
          </Stack>
        </Box>

        {textileMode ? (
          <Box
            sx={{
              ...card,
              width: { xs: "100%", md: 200 },
              flex: { xs: "0 0 auto", md: "0 0 200px" },
              minWidth: 0,
              minHeight: 0,
              ml: { md: 1 },
              mt: { xs: 1, md: 0 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              fontWeight={800}
              sx={{
                px: 1.5,
                pt: 1.35,
                pb: 1,
                letterSpacing: "-0.02em",
                fontSize: "0.92rem",
              }}
            >
              {t("skillAgent.rapport.runHistory", {
                defaultValue: "Histórico",
              })}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 1.5, pb: 1, lineHeight: 1.35 }}
            >
              {t("skillAgent.rapport.runHistoryHint", {
                defaultValue: "Versiones de este print. Pulsa para volver.",
              })}
            </Typography>
            <Box
              sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1, pb: 1 }}
            >
              {runHistory.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ px: 0.5, py: 1 }}
                >
                  {t("skillAgent.rapport.runHistoryEmpty", {
                    defaultValue: "Aún no hay generaciones.",
                  })}
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {runHistory.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        border: "1px solid",
                        borderColor: item.active ? accent : "divider",
                        borderRadius: 1.5,
                        overflow: "hidden",
                        bgcolor: item.active
                          ? alpha(accent, 0.08)
                          : "background.paper",
                        "&:hover": { borderColor: accent },
                      }}
                    >
                      <Box
                        onClick={() => onSelectHistory?.(item)}
                        sx={{ cursor: "pointer" }}
                      >
                        {item.thumbUrl ? (
                          <Box
                            component="img"
                            src={item.thumbUrl}
                            alt=""
                            sx={{
                              display: "block",
                              width: "100%",
                              height: 72,
                              objectFit: "cover",
                              bgcolor: "#f3efe8",
                            }}
                          />
                        ) : null}
                        <Box sx={{ px: 1, pt: 0.7, pb: 0.35 }}>
                          <Typography
                            variant="caption"
                            fontWeight={800}
                            sx={{ display: "block" }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {item.meta}
                          </Typography>
                        </Box>
                      </Box>
                      <Stack
                        direction="row"
                        spacing={0.25}
                        sx={{ px: 0.5, pb: 0.6 }}
                      >
                        <Button
                          size="small"
                          disabled={!item.svgText}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDownloadHistorySvg?.(item);
                          }}
                          sx={{
                            minWidth: 0,
                            px: 0.75,
                            fontWeight: 700,
                            textTransform: "none",
                            fontSize: "0.68rem",
                          }}
                        >
                          SVG
                        </Button>
                        <Button
                          size="small"
                          disabled={!item.thumbUrl}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDownloadHistoryPng?.(item);
                          }}
                          sx={{
                            minWidth: 0,
                            px: 0.75,
                            fontWeight: 700,
                            textTransform: "none",
                            fontSize: "0.68rem",
                          }}
                        >
                          PNG
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        ) : null}
      </Box>

      {!textileMode ? (
        <>
          <Box
            ref={splitRef}
            onPointerDown={(event) => {
              splitRef.current?.setPointerCapture(event.pointerId);
              splitRef.current.dataset.y = String(event.clientY);
              splitRef.current.dataset.h = String(workHeight);
            }}
            onPointerMove={(event) => {
              const grip = splitRef.current;
              if (!grip?.hasPointerCapture(event.pointerId)) return;
              const next =
                Number(grip.dataset.h) +
                (event.clientY - Number(grip.dataset.y));
              const max = Math.round(window.innerHeight * 0.86);
              setWorkHeight(Math.min(max, Math.max(280, next)));
            }}
            onDoubleClick={() =>
              setWorkHeight(
                Math.round(Math.min(760, window.innerHeight * 0.58)),
              )
            }
            sx={{
              height: 18,
              cursor: "row-resize",
              display: "grid",
              placeItems: "center",
              touchAction: "none",
              "&:hover .grip, &:active .grip": { bgcolor: accent, width: 88 },
            }}
          >
            <Box
              className="grip"
              sx={{
                width: 64,
                height: 4,
                borderRadius: 99,
                bgcolor: alpha(accent, 0.35),
                transition: "width 0.15s ease, background-color 0.15s ease",
              }}
            />
          </Box>

          <Box sx={{ ...card, height: "auto" }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ px: 1.75, pt: 1.5 }}
            >
              <StepMark n="3" color={accent} />
              <Typography fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
                {t("skillAgent.rapport.fichaTitle")}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 1.75, pt: 0.85, pb: 1.25, lineHeight: 1.45 }}
            >
              {t(
                isTile
                  ? "skillAgent.rapport.tileFichaHint"
                  : "skillAgent.rapport.interpretFichaHint",
              )}
              {isTile && tileStale ? (
                <Typography
                  variant="body2"
                  color="warning.main"
                  sx={{ px: 1.75, pb: 1.25 }}
                >
                  {t("skillAgent.rapport.stale")}
                </Typography>
              ) : null}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              sx={{ px: 1.75, pb: 1.75 }}
            >
              <TextField
                size="small"
                label={t("skillAgent.rapport.ancho")}
                value={ancho}
                onChange={(e) => setAncho(e.target.value)}
                sx={[pageFieldSx, { flex: 1 }]}
                placeholder="145"
              />
              <TextField
                size="small"
                label={t("skillAgent.rapport.widthCm")}
                value={rapportW}
                onChange={(e) => setRapportW(e.target.value)}
                sx={[pageFieldSx, { flex: 1 }]}
              />
              <TextField
                size="small"
                label={t("skillAgent.rapport.heightCm")}
                value={rapportH}
                onChange={(e) => setRapportH(e.target.value)}
                sx={[pageFieldSx, { flex: 1 }]}
              />
              <TextField
                select
                size="small"
                label={t("skillAgent.rapport.direction")}
                value={direccion}
                onChange={(e) => {
                  setDirectionTouched(true);
                  setDireccion(e.target.value);
                }}
                sx={[pageFieldSx, { flex: 1, minWidth: 140 }]}
              >
                <MenuItem value="horizontal">
                  {t("skillAgent.rapport.directionHorizontal")}
                </MenuItem>
                <MenuItem value="vertical">
                  {t("skillAgent.rapport.directionVertical")}
                </MenuItem>
              </TextField>
            </Stack>
          </Box>
        </>
      ) : null}
    </Stack>
  );
}

export default function SkillAgentRapportDetail({
  rapportId,
  onChanged,
  onDeleted,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fileRef = useRef(null);
  const canvasRef = useRef(null);
  const tilePreviewRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const photoBlobRef = useRef(null);
  const tileBlobRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedNote, setSavedNote] = useState("");
  const [row, setRow] = useState(null);
  const [titulo, setTitulo] = useState("");
  const [ancho, setAncho] = useState("");
  const [rapportW, setRapportW] = useState("");
  const [rapportH, setRapportH] = useState("");
  const [direccion, setDireccion] = useState("horizontal");
  const [directionTouched, setDirectionTouched] = useState(false);
  const [scaleCm, setScaleCm] = useState("10");
  const [tool, setTool] = useState("repeat");
  const [rule, setRule] = useState(null);
  const [repeat, setRepeat] = useState(null);
  const [corners, setCorners] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [tileUrl, setTileUrl] = useState("");
  const [tileStale, setTileStale] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [mode, setMode] = useState("generate");
  const [generating, setGenerating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [fileDrag, setFileDrag] = useState(false);
  const [intent, setIntent] = useState(null);
  const [intentSaving, setIntentSaving] = useState(false);
  const [weave, setWeaveState] = useState("auto");
  const [svgText, setSvgText] = useState("");
  const [resultSvgUrl, setResultSvgUrl] = useState("");
  const [seamlessPreviewUrl, setSeamlessPreviewUrl] = useState("");
  const seamlessPreviewRef = useRef("");
  const [hasSvg, setHasSvg] = useState(false);
  const [runHistory, setRunHistory] = useState([]);
  const [lastRunStats, setLastRunStats] = useState(null);
  const weaveRef = useRef("auto");
  const generateRef = useRef(async () => {});

  const photoUrlRef = useRef("");
  const tileHoldRef = useRef("");
  const historyRef = useRef({ undo: [], redo: [] });
  const marksRef = useRef({
    rule: null,
    repeat: null,
    corners: null,
    tool: "rule",
    mode: "generate",
  });
  const undoMarksRef = useRef(() => {});
  const redoMarksRef = useRef(() => {});
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const setPhotoUrl = (url) => {
    if (photoUrlRef.current && photoUrlRef.current !== url) {
      URL.revokeObjectURL(photoUrlRef.current);
    }
    photoUrlRef.current = url || "";
    setImageUrl(url || "");
  };

  const setHeldTileUrl = (url) => {
    if (tileHoldRef.current && tileHoldRef.current !== url) {
      URL.revokeObjectURL(tileHoldRef.current);
    }
    tileHoldRef.current = url || "";
    setTileUrl(url || "");
  };

  const setHeldSeamlessPreviewUrl = (url) => {
    if (seamlessPreviewRef.current && seamlessPreviewRef.current !== url) {
      URL.revokeObjectURL(seamlessPreviewRef.current);
    }
    seamlessPreviewRef.current = url || "";
    setSeamlessPreviewUrl(url || "");
  };

  useEffect(() => {
    return () => {
      if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
      if (tileHoldRef.current) URL.revokeObjectURL(tileHoldRef.current);
      if (seamlessPreviewRef.current) {
        URL.revokeObjectURL(seamlessPreviewRef.current);
      }
    };
  }, []);

  useEffect(() => {
    weaveRef.current = weave;
  }, [weave]);

  useEffect(() => {
    if (!svgText) {
      setResultSvgUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(
      new Blob([svgText], { type: "image/svg+xml" }),
    );
    setResultSvgUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [svgText]);

  useEffect(() => {
    setRunHistory((prev) => {
      prev.forEach((item) => {
        if (item.thumbUrl) URL.revokeObjectURL(item.thumbUrl);
      });
      return [];
    });
    setSvgText("");
    setHasSvg(false);
    setHeldSeamlessPreviewUrl("");
    setLastRunStats(null);
  }, [rapportId]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getSkillAgentRapportById(rapportId);
        if (cancel) return;
        setRow(data);
        setTitulo(data.Titulo || "");
        setAncho(data.AnchoPiezaCm ?? "");
        setRapportW(data.RapportAnchoCm ?? "");
        setRapportH(data.RapportAltoCm ?? "");
        setDireccion(data.DireccionHilo || "horizontal");
        setDirectionTouched(Boolean(data.DireccionHilo));
        setScaleCm(data.ScaleCm ?? "10");
        const marks = parseMarks(data.MarksJson);
        setRule(marks.rule);
        setRepeat(marks.repeat);
        setCorners(marks.corners);
        setIntent(marks.intent || rapportIntent(data));
        setMode(rapportToolKind(data) === "tartan" ? "crop" : "generate");
        if (marks?.spec?.weave === "plain" || marks?.spec?.weave === "twill") {
          setWeaveState(marks.spec.weave);
          weaveRef.current = marks.spec.weave;
        }
        setSvgText("");
        setHasSvg(Boolean(data.hasSvg || marks?.driveSvgFileId));
        if (data.hasSvg || marks?.driveSvgFileId || data.hasTile) {
          try {
            const svgBlob = await fetchSkillAgentRapportBlob(rapportId, "svg");
            if (!cancel && svgBlob?.size > 32) {
              setSvgText(await svgBlob.text());
              setHasSvg(true);
            }
          } catch {
            /* svg opcional al abrir */
          }
        }
        if (data.hasPhoto) {
          try {
            const blob = await fetchSkillAgentRapportBlob(rapportId, "photo");
            if (cancel) return;
            if (blob && blob.size > 32) {
              setPhotoUrl(URL.createObjectURL(blob));
            } else {
              setError(t("skillAgent.rapport.photoMissing"));
            }
          } catch {
            if (!cancel) setError(t("skillAgent.rapport.photoMissing"));
          }
        }
        if (data.hasTile) {
          try {
            const blob = await fetchSkillAgentRapportBlob(rapportId, "tile");
            if (cancel) return;
            if (blob && blob.size > 32) {
              // No reseellar al abrir: un half-drop de ramo no debe
              // pasar otra vez por sellados pensados para tejido continuo.
              tileBlobRef.current = blob;
              setHeldTileUrl(URL.createObjectURL(blob));
              setTileStale(false);
            }
          } catch {
            /* tile opcional */
          }
        }
      } catch (err) {
        if (!cancel) {
          setError(
            err?.response?.data?.error || t("skillAgent.rapport.loadError"),
          );
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [rapportId, t]);

  useEffect(() => {
    marksRef.current = { rule, repeat, corners, tool, mode };
  }, [rule, repeat, corners, tool, mode]);

  const syncHistoryButtons = () => {
    setCanUndo(historyRef.current.undo.length > 0);
    setCanRedo(historyRef.current.redo.length > 0);
  };

  const applyMarksSnap = (snap) => {
    if (!snap) return;
    setRule(snap.rule);
    setRepeat(snap.repeat);
    setCorners(snap.corners);
    if (snap.tool) setTool(snap.tool);
    if (snap.mode) setMode(snap.mode);
    setTileStale(true);
  };

  const pushUndo = () => {
    const snap = JSON.parse(JSON.stringify(marksRef.current));
    historyRef.current.undo = pushRapportHistory(historyRef.current.undo, snap);
    historyRef.current.redo = [];
    syncHistoryButtons();
  };

  const undoMarks = () => {
    const { stack, item } = popRapportHistory(historyRef.current.undo);
    if (!item) return;
    historyRef.current.undo = stack;
    historyRef.current.redo = pushRapportHistory(
      historyRef.current.redo,
      JSON.parse(JSON.stringify(marksRef.current)),
    );
    applyMarksSnap(item);
    syncHistoryButtons();
  };

  const redoMarks = () => {
    const { stack, item } = popRapportHistory(historyRef.current.redo);
    if (!item) return;
    historyRef.current.redo = stack;
    historyRef.current.undo = pushRapportHistory(
      historyRef.current.undo,
      JSON.parse(JSON.stringify(marksRef.current)),
    );
    applyMarksSnap(item);
    syncHistoryButtons();
  };

  undoMarksRef.current = undoMarks;
  redoMarksRef.current = redoMarks;

  useEffect(() => {
    const onKey = (event) => {
      const tag = String(event.target?.tagName || "");
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        event.target?.isContentEditable
      ) {
        return;
      }
      const mod = event.ctrlKey || event.metaKey;
      if (!mod) return;
      const key = String(event.key || "").toLowerCase();
      if (key === "z" && !event.shiftKey) {
        event.preventDefault();
        undoMarksRef.current();
        return;
      }
      if (key === "y" || (key === "z" && event.shiftKey)) {
        event.preventDefault();
        redoMarksRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!imageUrl) return undefined;
    let cancel = false;
    const img = new Image();
    img.onload = () => {
      if (cancel) return;
      imgRef.current = img;
      setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.src = imageUrl;
    return () => {
      cancel = true;
    };
  }, [imageUrl]);

  useEffect(() => {
    if (!tileUrl) return undefined;
    let cancel = false;
    let observer = null;
    let raf = 0;
    let lastW = 0;
    let lastH = 0;
    const img = new Image();
    const paint = () => {
      if (cancel) return;
      const canvas = tilePreviewRef.current;
      if (!canvas) return;
      const cssW = Math.round(canvas.clientWidth);
      const cssH = Math.round(canvas.clientHeight);
      if (cssW < 8 || cssH < 8) return;
      if (cssW === lastW && cssH === lastH) return;
      lastW = cssW;
      lastH = cssH;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      const ctx = canvas.getContext("2d");
      // createPattern + setTransform(dpr) deja huecos de 1px → se ve el
      // fondo crema como rejilla. Pintamos en píxeles de dispositivo.
      // Seamless textile ya viene tileable del motor: NO forceWrapEdges
      // (rompe el módulo IA y deja el corte en cruz).
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const tw = img.naturalWidth || 1;
      const th = img.naturalHeight || 1;
      const aspect = tw / th;
      const fit = Math.min(
        canvas.width / 2.2,
        canvas.height / 1.6,
        Math.max(tw, th),
      );
      const cellW =
        aspect >= 1
          ? Math.max(32, Math.round(fit))
          : Math.max(32, Math.round(fit * aspect));
      const cellH =
        aspect >= 1
          ? Math.max(32, Math.round(fit / aspect))
          : Math.max(32, Math.round(fit));
      const off = document.createElement("canvas");
      off.width = cellW;
      off.height = cellH;
      const octx = off.getContext("2d", { willReadFrequently: true });
      octx.imageSmoothingEnabled = cellW < tw || cellH < th;
      octx.imageSmoothingQuality = "high";
      octx.drawImage(img, 0, 0, cellW, cellH);
      let fill = "#f3efe8";
      try {
        const sample = octx.getImageData(0, 0, 1, 1).data;
        fill = `rgb(${sample[0]},${sample[1]},${sample[2]})`;
      } catch {
        /* ignore */
      }
      ctx.fillStyle = fill;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      for (let y = 0; y < canvas.height + cellH; y += cellH) {
        for (let x = 0; x < canvas.width + cellW; x += cellW) {
          ctx.drawImage(off, x, y, cellW + 1, cellH + 1);
        }
      }
    };
    img.onload = () => {
      if (cancel) return;
      const watch = () => {
        const canvas = tilePreviewRef.current;
        if (!canvas) {
          raf = requestAnimationFrame(watch);
          return;
        }
        observer = new ResizeObserver(() => paint());
        observer.observe(canvas);
        paint();
      };
      watch();
    };
    img.src = tileUrl;
    return () => {
      cancel = true;
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [tileUrl]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !imgSize.w) return;
    canvas.width = imgSize.w;
    canvas.height = imgSize.h;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, imgSize.w, imgSize.h);
    const viewW = canvas.clientWidth || canvas.width;
    const handle = Math.max(10, (16 * canvas.width) / viewW);
    ctx.lineWidth = Math.max(2, canvas.width / 500);
    if (rule) {
      ctx.strokeStyle = "#eab308";
      ctx.beginPath();
      ctx.moveTo(rule.x1, rule.y1);
      ctx.lineTo(rule.x2, rule.y2);
      ctx.stroke();
    }
    const rect = normalizeRect(repeat);
    const pts = parseCorners(corners) || cornersFromRect(rect);
    if (pts) {
      ctx.strokeStyle = "#0284c7";
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      pts.slice(1).forEach((pt) => ctx.lineTo(pt.x, pt.y));
      ctx.closePath();
      ctx.stroke();
      ctx.fillStyle = "#0284c7";
      pts.forEach((pt) => {
        ctx.fillRect(pt.x - handle / 2, pt.y - handle / 2, handle, handle);
      });
    }
  }, [imageUrl, imgSize, rule, repeat, corners]);

  const measured = rapportCmFromMarks(rule, scaleCm, repeat, corners);

  const pointerPos = (event) => {
    const canvas = canvasRef.current;
    if (!canvas || !imgSize.w) return null;
    const box = canvas.getBoundingClientRect();
    if (!box.width || !box.height) return null;
    return {
      x: Math.max(
        0,
        Math.min(
          imgSize.w,
          ((event.clientX - box.left) / box.width) * imgSize.w,
        ),
      ),
      y: Math.max(
        0,
        Math.min(
          imgSize.h,
          ((event.clientY - box.top) / box.height) * imgSize.h,
        ),
      ),
    };
  };

  const onPointerDown = (event) => {
    const p = pointerPos(event);
    if (!p) return;
    pushUndo();
    event.currentTarget.setPointerCapture(event.pointerId);
    if (tool === "rule") {
      dragRef.current = { mode: "rule", x: p.x, y: p.y };
      setRule({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
      return;
    }
    const canvas = canvasRef.current;
    const viewW = canvas.clientWidth || imgSize.w;
    const handle = Math.max(12, (18 * imgSize.w) / viewW);
    const pts = parseCorners(corners) || cornersFromRect(repeat);
    const corner = hitQuadCorner(pts, p.x, p.y, handle);
    if (corner >= 0) {
      dragRef.current = { mode: "corner", index: corner, corners: pts };
      return;
    }
    const hit = hitRepeat(normalizeRect(repeat), p.x, p.y, handle);
    if (hit === "move" || hit === "resize") {
      dragRef.current = {
        mode: "move",
        corners: pts,
        x: p.x,
        y: p.y,
      };
      return;
    }
    dragRef.current = { mode: "draw", x: p.x, y: p.y };
    const seed = { x: p.x, y: p.y, w: 1, h: 1 };
    setRepeat(seed);
    setCorners(cornersFromRect(seed));
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    const p = pointerPos(event);
    if (!drag || !p) return;
    setTileStale(true);
    if (drag.mode === "rule") {
      setRule({ x1: drag.x, y1: drag.y, x2: p.x, y2: p.y });
      if (!directionTouched) {
        const dir = warpFromScaleLine(drag.x, drag.y, p.x, p.y);
        if (dir) setDireccion(dir);
      }
      return;
    }
    if (drag.mode === "draw") {
      const n = normalizeRect({
        x: drag.x,
        y: drag.y,
        w: p.x - drag.x,
        h: p.y - drag.y,
      });
      setRepeat(n);
      setCorners(cornersFromRect(n));
      return;
    }
    if (drag.mode === "corner") {
      const next = drag.corners.map((c, i) =>
        i === drag.index ? { x: p.x, y: p.y } : c,
      );
      setCorners(next);
      setRepeat(boundingRectFromCorners(next));
      return;
    }
    if (drag.mode === "move") {
      const dx = p.x - drag.x;
      const dy = p.y - drag.y;
      const next = drag.corners.map((c) => ({ x: c.x + dx, y: c.y + dy }));
      setCorners(next);
      setRepeat(boundingRectFromCorners(next));
    }
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const applyRotation = async (radians) => {
    const img = imgRef.current;
    if (!img?.naturalWidth || !Number.isFinite(radians) || radians === 0)
      return;
    pushUndo();
    setError("");
    const srcW = img.naturalWidth;
    const srcH = img.naturalHeight;
    const blob = await rotateImageToBlob(img, radians);
    if (!blob) {
      setError(t("skillAgent.rapport.photoError"));
      return;
    }
    photoBlobRef.current = blob;
    tileBlobRef.current = null;
    setRule(mapRuleAfterRotation(rule, srcW, srcH, radians));
    const nextCorners = mapCornersAfterRotation(
      corners || cornersFromRect(repeat),
      srcW,
      srcH,
      radians,
    );
    setCorners(nextCorners);
    setRepeat(
      boundingRectFromCorners(nextCorners) ||
        mapRectAfterRotation(repeat, srcW, srcH, radians),
    );
    setHeldTileUrl("");
    setHeldSeamlessPreviewUrl("");
    setSvgText("");
    setHasSvg(false);
    setTileStale(true);
    setPhotoUrl(URL.createObjectURL(blob));
    try {
      await persistPhoto(blob);
    } catch {
      /* se guarda al pulsar Guardar */
    }
  };

  const onStraighten = async () => {
    const rad = rule
      ? deskewRadiansFromRule(rule.x1, rule.y1, rule.x2, rule.y2)
      : null;
    if (rad == null || Math.abs(rad) < 1e-4) return;
    await applyRotation(rad);
  };

  const persistPhoto = async (blob) => {
    const form = new FormData();
    form.append("Titulo", String(titulo || row?.Titulo || "Rapport").trim());
    form.append("photo", blob, "photo.jpg");
    const updated = await updateSkillAgentRapport(rapportId, form);
    setRow(updated);
    photoBlobRef.current = null;
    return updated;
  };

  const ingestPhotoFile = async (file) => {
    if (!file || !String(file.type || "").startsWith("image/")) {
      setError(t("skillAgent.rapport.photoError"));
      return;
    }
    setError("");
    try {
      const blob = await downscaleFile(file);
      photoBlobRef.current = blob;
      tileBlobRef.current = null;
      setRule(null);
      setRepeat(null);
      setCorners(null);
      setHeldTileUrl("");
      setHeldSeamlessPreviewUrl("");
      setSvgText("");
      setHasSvg(false);
      setTileStale(false);
      setPhotoUrl(URL.createObjectURL(blob));
      await persistPhoto(blob);
      setSavedNote(t("skillAgent.rapport.photoSaved"));
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.error || t("skillAgent.rapport.photoError"),
      );
    }
  };

  const onPickFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await ingestPhotoFile(file);
  };

  const onFileDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    if (!fileDrag) setFileDrag(true);
  };

  const onFileDragLeave = (event) => {
    event.preventDefault();
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setFileDrag(false);
  };

  const onFileDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setFileDrag(false);
    const files = event.dataTransfer?.files;
    let file = null;
    if (files?.length) {
      file = Array.from(files).find((item) =>
        String(item.type || "").startsWith("image/"),
      );
    }
    if (file) await ingestPhotoFile(file);
  };

  const cropSwatchBlob = async () => {
    const img = imgRef.current;
    const rect =
      boundingRectFromCorners(parseCorners(corners)) || normalizeRect(repeat);
    if (!img?.naturalWidth || !rect || rect.w < 16 || rect.h < 16) return null;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(8, Math.round(rect.w));
    canvas.height = Math.max(8, Math.round(rect.h));
    canvas
      .getContext("2d")
      .drawImage(
        img,
        rect.x,
        rect.y,
        rect.w,
        rect.h,
        0,
        0,
        canvas.width,
        canvas.height,
      );
    return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
  };

  const readPhotoRgba = () => {
    const img = imgRef.current;
    if (!img?.naturalWidth) return null;
    const rect =
      boundingRectFromCorners(parseCorners(corners)) || normalizeRect(repeat);
    const useRect = !!(rect && rect.w >= 16 && rect.h >= 16);
    const sw = useRect ? rect.w : img.naturalWidth;
    const sh = useRect ? rect.h : img.naturalHeight;
    const scale = Math.min(1, 900 / Math.max(sw, sh));
    const dw = Math.max(32, Math.round(sw * scale));
    const dh = Math.max(32, Math.round(sh * scale));
    const canvas = document.createElement("canvas");
    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (useRect) {
      ctx.drawImage(img, rect.x, rect.y, rect.w, rect.h, 0, 0, dw, dh);
    } else {
      ctx.drawImage(img, 0, 0, dw, dh);
    }
    return {
      rgba: new Uint8ClampedArray(ctx.getImageData(0, 0, dw, dh).data),
      w: dw,
      h: dh,
    };
  };

  /** Half-drop en el navegador: no depende del AI-SERVICE. */
  const clientHalfDropTileBlob = async () => {
    const photo = readPhotoRgba();
    if (!photo) return null;
    const tiled = motifHalfDropSeamless(photo.rgba, photo.w, photo.h);
    if (tiled.width === photo.w && tiled.height === photo.h) {
      // No recortó el ramo: no es un half-drop útil.
      console.warn("[interpret] half-drop no recortó el motivo");
    }
    const out = document.createElement("canvas");
    out.width = tiled.width;
    out.height = tiled.height;
    out
      .getContext("2d")
      .putImageData(new ImageData(tiled.rgba, tiled.width, tiled.height), 0, 0);
    return new Promise((resolve) => out.toBlob(resolve, "image/png"));
  };

  const photoBlobForAi = async () => {
    const cropped = await cropSwatchBlob();
    if (cropped) return cropped;
    if (photoBlobRef.current) return photoBlobRef.current;
    const img = imgRef.current;
    if (!img?.naturalWidth) return null;
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext("2d").drawImage(img, 0, 0);
    return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
  };

  const onTextileGenerate = async () => {
    setError("");
    setSavedNote("");
    setGenerating(true);
    const startedAt = Date.now();
    const engine = rapportToolKind(row) === "tartan" ? "tartan" : "seamless";
    // Como VectorTon: default "auto" → liso si 2 tintas (vichy), sarga si más.
    const weaveNow = weaveRef.current || "auto";
    // Mismo fallback que los sliders (150): si no se manda cm, el motor usa
    // sampleThreads/30 ≈ 0.6 cm y el preview sale minúsculo.
    const widthNow = Number(rapportW) || Number(ancho) || 150;
    const heightNow = Number(rapportH) || 150;
    try {
      // No re-subir foto ni Drive en Generar: solo el motor (como VectorTon).
      const cropBlob = await photoBlobForAi();
      if (!cropBlob) {
        setError(
          t("skillAgent.rapport.needPhoto", {
            defaultValue: "Sube primero la foto de la muestra.",
          }),
        );
        return;
      }
      const generated = await generateSkillAgentRapportTile(
        rapportId,
        cropBlob,
        {
          engine,
          widthCm: widthNow,
          heightCm: heightNow,
          weave: engine === "tartan" ? weaveNow : undefined,
        },
      );
      // Tartán: SVG en JSON. Seamless foto: NO cargar SVG (pesa; preview = JPEG 3×3).
      let nextSvg = generated?.svg ? String(generated.svg) : "";
      if (!nextSvg && generated?.hasSvg && engine === "tartan") {
        try {
          const svgBlob = await fetchSkillAgentRapportBlob(rapportId, "svg");
          if (svgBlob?.size > 32) nextSvg = await svgBlob.text();
        } catch (svgErr) {
          console.warn("[textile] SVG fetch:", svgErr);
        }
      }
      setSvgText(nextSvg);
      setHasSvg(
        Boolean(nextSvg) ||
          (engine === "tartan" && Boolean(generated?.hasSvg)) ||
          (engine === "seamless" && Boolean(generated?.hasSvg)),
      );
      const pathCount = nextSvg
        ? (nextSvg.match(/<path\b/gi) || []).length +
          (nextSvg.match(/<rect\b/gi) || []).length
        : null;
      const noteLine = Array.isArray(generated?.notes)
        ? generated.notes.find((n) =>
            /IA|seamless|All-over|offset/i.test(String(n)),
          )
        : null;
      setLastRunStats({
        engineLabel: engine === "tartan" ? "Tartán" : "Seamless",
        version: generated?.vectorNote
          ? String(generated.vectorNote).replace(/^SVG:\s*/i, "")
          : "—",
        elapsedMs: Date.now() - startedAt,
        svgBytes: nextSvg ? new Blob([nextSvg]).size : null,
        pathCount,
        widthCm:
          widthNow ||
          generated?.size?.widthCm ||
          generated?.spec?.widthCm ||
          "—",
        heightCm:
          heightNow ||
          generated?.size?.heightCm ||
          generated?.spec?.heightCm ||
          "—",
      });
      const b64ToBlob = (b64, mime) => {
        const bin = atob(String(b64));
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
        return new Blob([bytes], { type: mime });
      };
      // VectorTon: resultado visible = previewJpeg 3×3. El módulo PNG vive en el
      // servidor (GET /tile); no viaja en JSON (se cortaba y la descarga salía mal).
      if (engine === "seamless" && generated?.previewJpegBase64) {
        setHeldSeamlessPreviewUrl(
          URL.createObjectURL(
            b64ToBlob(generated.previewJpegBase64, "image/jpeg"),
          ),
        );
      } else {
        setHeldSeamlessPreviewUrl("");
      }
      let blob = null;
      if (engine === "seamless") {
        // Fuente de verdad: tile.png ya heal+lock en disco (Cambio 2).
        try {
          blob = await fetchSkillAgentRapportBlob(rapportId, "tile");
        } catch {
          blob = null;
        }
        if ((!blob || blob.size < 32) && generated?.imageBase64) {
          blob = b64ToBlob(
            generated.imageBase64,
            generated?.mime || "image/png",
          );
        }
      } else if (generated?.imageBase64) {
        blob = b64ToBlob(generated.imageBase64, generated?.mime || "image/png");
      } else {
        try {
          blob = await fetchSkillAgentRapportBlob(rapportId, "tile");
        } catch {
          blob = null;
        }
      }
      // Tartán: basta el SVG. Seamless: módulo PNG o al menos preview 3×3.
      if (
        (!blob || blob.size < 32) &&
        engine === "seamless" &&
        !generated?.previewJpegBase64
      ) {
        setError(t("skillAgent.rapport.interpretError"));
        return;
      }
      if (blob && blob.size >= 32) {
        tileBlobRef.current = blob;
        const previewUrl = URL.createObjectURL(blob);
        const thumbUrl = URL.createObjectURL(blob);
        setHeldTileUrl(previewUrl);
        setRunHistory((prev) => {
          const trimmed = prev.slice(0, 11);
          const dropped = prev.slice(11);
          dropped.forEach((item) => {
            if (item.thumbUrl) URL.revokeObjectURL(item.thumbUrl);
          });
          const resolvedWeave =
            generated?.weave === "plain" || generated?.weave === "twill"
              ? generated.weave
              : weaveNow;
          const weaveLabel =
            engine !== "tartan"
              ? "Seamless"
              : resolvedWeave === "plain"
                ? "Liso / vichy"
                : resolvedWeave === "twill"
                  ? "Sarga"
                  : "Auto";
          return [
            {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              active: true,
              label: weaveLabel,
              meta: [rapportW || ancho || "—", "×", rapportH || "—", "cm"].join(
                " ",
              ),
              weave: weaveNow,
              widthCm: rapportW,
              heightCm: rapportH,
              svgText: nextSvg,
              thumbUrl,
              at: Date.now(),
            },
            ...trimmed.map((item) => ({ ...item, active: false })),
          ];
        });
      } else if (nextSvg) {
        // Histórico solo con SVG (tartán sin PNG).
        const resolvedWeave =
          generated?.weave === "plain" || generated?.weave === "twill"
            ? generated.weave
            : weaveNow;
        setRunHistory((prev) => {
          const trimmed = prev.slice(0, 11);
          return [
            {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              active: true,
              label:
                resolvedWeave === "plain"
                  ? "Liso / vichy"
                  : resolvedWeave === "twill"
                    ? "Sarga"
                    : "Auto",
              meta: [rapportW || ancho || "—", "×", rapportH || "—", "cm"].join(
                " ",
              ),
              weave: weaveNow,
              widthCm: rapportW,
              heightCm: rapportH,
              svgText: nextSvg,
              thumbUrl: "",
              at: Date.now(),
            },
            ...trimmed.map((item) => ({ ...item, active: false })),
          ];
        });
      }
      setTileStale(false);
      setRow(generated);
      setSavedNote(
        nextSvg || blob || generated?.previewJpegBase64
          ? noteLine ||
              generated?.vectorNote ||
              (engine === "tartan"
                ? t("skillAgent.rapport.tartanReady", {
                    defaultValue:
                      "Tartán listo. Pulsa Guardar para subirlo a Drive.",
                  })
                : t("skillAgent.rapport.seamlessReady", {
                    defaultValue:
                      "Seamless listo. Pulsa Guardar para subirlo a Drive.",
                  }))
          : t("skillAgent.rapport.tartanReadyNoSvg", {
              defaultValue: "Listo. Si Descargar SVG falla, vuelve a generar.",
            }),
      );
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          t("skillAgent.rapport.interpretError"),
      );
    } finally {
      setGenerating(false);
    }
  };

  generateRef.current = onTextileGenerate;

  const setWeave = (next) => {
    const value =
      next === "plain" || next === "twill" || next === "auto" ? next : "auto";
    if (value === weaveRef.current) return;
    setWeaveState(value);
    weaveRef.current = value;
  };

  const onSelectHistory = (item) => {
    if (!item) return;
    const histWeave =
      item.weave === "plain" || item.weave === "twill" || item.weave === "auto"
        ? item.weave
        : "auto";
    setWeaveState(histWeave);
    weaveRef.current = histWeave;
    if (item.widthCm != null && item.widthCm !== "") {
      setRapportW(String(item.widthCm));
    }
    if (item.heightCm != null && item.heightCm !== "") {
      setRapportH(String(item.heightCm));
    }
    if (item.svgText) {
      setSvgText(item.svgText);
      setHasSvg(true);
    }
    if (item.thumbUrl) setHeldTileUrl(item.thumbUrl);
    setRunHistory((prev) =>
      prev.map((rowItem) => ({
        ...rowItem,
        active: rowItem.id === item.id,
      })),
    );
    setSavedNote(
      t("skillAgent.rapport.runHistoryRestored", {
        defaultValue: `Histórico: ${item.label}`,
      }),
    );
  };

  const historyFileBase = (item) => {
    const base = String(titulo || row?.Titulo || "rapport")
      .trim()
      .replace(/[<>:"/\\|?*]+/g, "-");
    const tag = String(item?.label || "run")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-");
    return `${base || "rapport"}-${tag || "run"}`;
  };

  const onDownloadHistorySvg = (item) => {
    if (!item?.svgText) return;
    const blob = new Blob([item.svgText], {
      type: "image/svg+xml;charset=utf-8",
    });
    downloadUrl(URL.createObjectURL(blob), `${historyFileBase(item)}.svg`);
  };

  const onDownloadHistoryPng = (item) => {
    if (!item?.thumbUrl) return;
    downloadBlobUrl(item.thumbUrl, `${historyFileBase(item)}.png`);
  };

  const onInterpret = onTextileGenerate;

  const onPickIntent = async (nextIntent) => {
    if (nextIntent !== "cloth" && nextIntent !== "invent") return;
    setIntentSaving(true);
    setError("");
    try {
      const marks = parseMarks(row?.MarksJson);
      const form = new FormData();
      form.append("Titulo", String(titulo || row?.Titulo || "Rapport").trim());
      form.append(
        "MarksJson",
        JSON.stringify({
          rule: marks.rule ?? rule,
          repeat: normalizeRect(marks.repeat ?? repeat),
          corners:
            parseCorners(marks.corners ?? corners) ||
            cornersFromRect(marks.repeat ?? repeat),
          mode: "generate",
          tool: "interpretacion",
          intent: nextIntent,
        }),
      );
      const updated = await updateSkillAgentRapport(rapportId, form);
      setRow(updated);
      setIntent(nextIntent);
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.rapport.saveError"));
    } finally {
      setIntentSaving(false);
    }
  };

  const buildTileBlob = async () => {
    const img = imgRef.current;
    const pts = parseCorners(corners) || cornersFromRect(repeat);
    const size = quadSizePx(pts);
    if (!img || !size) return null;
    const fromFicha = tilePxFromRapportCm(
      rapportW,
      rapportH,
      rapportCmFromMarks(rule, scaleCm, repeat, corners).pxPerCm,
    );
    const destW = fromFicha?.w || Math.max(8, Math.round(size.w));
    const destH = fromFicha?.h || Math.max(8, Math.round(size.h));
    const srcCanvas = document.createElement("canvas");
    srcCanvas.width = img.naturalWidth;
    srcCanvas.height = img.naturalHeight;
    const srcCtx = srcCanvas.getContext("2d", { willReadFrequently: true });
    srcCtx.drawImage(img, 0, 0);
    const srcData = srcCtx.getImageData(
      0,
      0,
      srcCanvas.width,
      srcCanvas.height,
    );
    const warped = warpQuadToRgba(
      srcData.data,
      srcCanvas.width,
      srcCanvas.height,
      pts,
      destW,
      destH,
    );
    const seamless = makeSeamless(warped, destW, destH);
    const tile = document.createElement("canvas");
    tile.width = destW;
    tile.height = destH;
    tile
      .getContext("2d")
      .putImageData(new ImageData(seamless, destW, destH), 0, 0);
    return new Promise((resolve) => tile.toBlob(resolve, "image/png"));
  };

  const applyMeasure = () => {
    const next = rapportCmFromMarks(rule, scaleCm, repeat, corners);
    if (next.widthCm != null) {
      setRapportW(String(next.widthCm));
      setRapportH(String(next.heightCm));
    }
    if (next.direccion && !directionTouched) setDireccion(next.direccion);
    return next;
  };

  const onInterpretClick = () => {
    setMode("generate");
    onTextileGenerate();
  };

  const onGenerateClick = () => {
    setMode("crop");
    setError("");
    setSavedNote("");
    onTextileGenerate();
  };

  const onGenerate = onTextileGenerate;

  const onTitleCommit = async (next) => {
    const name = String(next || "").trim();
    if (!name || !rapportId) return;
    if (name === String(row?.Titulo || "").trim()) return;
    setSaving(true);
    setError("");
    try {
      const form = new FormData();
      form.append("Titulo", name);
      const updated = await updateSkillAgentRapport(rapportId, form);
      setRow(updated);
      setTitulo(updated?.Titulo || name);
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.rapport.saveError"));
      setTitulo(row?.Titulo || "");
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    const name = String(titulo || "").trim();
    if (!name) {
      setError(t("skillAgent.rapport.titleRequired"));
      return;
    }
    setSaving(true);
    setError("");
    setSavedNote("");
    try {
      let tileBlob = tileBlobRef.current;
      let widthCm = rapportW;
      let heightCm = rapportH;
      const rebuilding = Boolean(
        rapportToolKind(row) === "tartan" &&
        imageUrl &&
        repeat &&
        (tileStale || !tileBlob),
      );
      if (rebuilding) {
        tileBlob = await buildTileBlob();
        if (tileBlob) {
          tileBlobRef.current = tileBlob;
          setHeldTileUrl(URL.createObjectURL(tileBlob));
          setTileStale(false);
          const measure = rapportCmFromMarks(rule, scaleCm, repeat, corners);
          if (measure.widthCm != null) {
            widthCm = measure.widthCm;
            heightCm = measure.heightCm;
            setRapportW(String(widthCm));
            setRapportH(String(heightCm));
          }
          if (measure.direccion && !directionTouched)
            setDireccion(measure.direccion);
        }
      }
      const form = new FormData();
      form.append("Titulo", name);
      form.append("AnchoPiezaCm", ancho === "" ? "" : String(ancho));
      form.append(
        "RapportAnchoCm",
        widthCm === "" || widthCm == null ? "" : String(widthCm),
      );
      form.append(
        "RapportAltoCm",
        heightCm === "" || heightCm == null ? "" : String(heightCm),
      );
      form.append("DireccionHilo", direccion || "");
      form.append("ScaleCm", scaleCm === "" ? "" : String(scaleCm));
      form.append(
        "MarksJson",
        JSON.stringify({
          rule,
          repeat: normalizeRect(repeat),
          corners: parseCorners(corners) || cornersFromRect(repeat),
          mode: "textile",
          tool: rapportToolKind(row),
          engine: rapportToolKind(row),
        }),
      );
      if (photoBlobRef.current) {
        form.append("photo", photoBlobRef.current, "photo.jpg");
      }
      if (tileBlob) form.append("tile", tileBlob, "tile.png");
      if (svgText) {
        form.append(
          "tileSvg",
          new Blob([svgText], { type: "image/svg+xml" }),
          "tile.svg",
        );
      }
      const updated = await updateSkillAgentRapport(rapportId, form);
      setRow(updated);
      photoBlobRef.current = null;
      if (tileBlob) tileBlobRef.current = null;
      setSavedNote(t("skillAgent.rapport.saved"));
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.rapport.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = () => setConfirmDelete(true);

  const onConfirmDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await deleteSkillAgentRapport(rapportId);
      setConfirmDelete(false);
      onDeleted?.();
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.rapport.saveError"));
      setDeleting(false);
    }
  };

  const downloadUrl = (url, filename) => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
  };

  const downloadSvg = async () => {
    setError("");
    try {
      let text = svgText;
      if (!text) {
        const svgBlob = await fetchSkillAgentRapportBlob(rapportId, "svg");
        if (svgBlob?.size > 32) {
          text = await svgBlob.text();
          setSvgText(text);
          setHasSvg(true);
        }
      }
      if (!text) {
        setError(
          t("skillAgent.rapport.svgMissing", {
            defaultValue: "No hay SVG para descargar. Vuelve a generar.",
          }),
        );
        return;
      }
      const blob = new Blob([text], {
        type: "image/svg+xml;charset=utf-8",
      });
      downloadUrl(URL.createObjectURL(blob), `${titulo || "rapport"}-tile.svg`);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.rapport.svgMissing", {
            defaultValue: "No hay SVG para descargar. Vuelve a generar.",
          }),
      );
    }
  };

  /** PNG del módulo heal+lock desde el servidor (como el SVG embebido de VectorTon). */
  const downloadTilePng = async () => {
    setError("");
    try {
      let blob = null;
      try {
        blob = await fetchSkillAgentRapportBlob(rapportId, "tile");
      } catch {
        blob = null;
      }
      if ((!blob || blob.size < 32) && tileBlobRef.current) {
        blob = tileBlobRef.current;
      }
      if (!blob || blob.size < 32) {
        setError(
          t("skillAgent.rapport.tileMissing", {
            defaultValue: "No hay PNG del módulo. Vuelve a generar.",
          }),
        );
        return;
      }
      const url = URL.createObjectURL(blob);
      downloadUrl(url, `${titulo || "rapport"}-tile.png`);
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.rapport.tileMissing", {
            defaultValue: "No hay PNG del módulo. Vuelve a generar.",
          }),
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: "grid", placeItems: "center", minHeight: 240 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const isTile = rapportToolKind(row) === "tartan";
  const needsIntent = false;
  const branch = [row?.CustomerName, row?.Temporada]
    .filter(Boolean)
    .join(" · ");

  const drivePath =
    row?.drivePath ||
    rapportDrivePathLabel({
      ...row,
      Titulo: titulo || row?.Titulo,
    });

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, position: "relative" }}>
      <SkillAgentRapportIntentDialog
        open={needsIntent}
        submitting={intentSaving}
        onPick={onPickIntent}
      />
      {generating ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            bgcolor: alpha("#f6f3ee", 0.82),
            backdropFilter: "blur(6px)",
          }}
        >
          <CircularProgress size={36} />
          <Typography fontWeight={800}>
            {t(
              isTile
                ? "skillAgent.rapport.generatingTartan"
                : "skillAgent.rapport.generatingSeamless",
              {
                defaultValue: isTile
                  ? "Generando tartán…"
                  : "Generando seamless…",
              },
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              isTile
                ? "skillAgent.rapport.textileGeneratingHintTartan"
                : "skillAgent.rapport.textileGeneratingHintSeamless",
              {
                defaultValue: isTile
                  ? "Vectorton textile · tartán local."
                  : "Vectorton · IA tileable con fidelidad a la foto (~40 s).",
              },
            )}
          </Typography>
        </Box>
      ) : null}

      <Stack spacing={1.75}>
        <InterpretLayout
          t={t}
          theme={theme}
          imageUrl={imageUrl}
          tileUrl={tileUrl}
          fileDrag={fileDrag}
          tool={tool}
          setTool={setTool}
          rule={rule}
          scaleCm={scaleCm}
          setScaleCm={setScaleCm}
          setTileStale={setTileStale}
          measured={measured}
          generating={generating}
          saving={saving}
          canUndo={canUndo}
          canRedo={canRedo}
          undoMarksRef={undoMarksRef}
          redoMarksRef={redoMarksRef}
          fileRef={fileRef}
          imgRef={imgRef}
          canvasRef={canvasRef}
          tilePreviewRef={tilePreviewRef}
          setImgSize={setImgSize}
          imgSize={imgSize}
          onFileDragOver={onFileDragOver}
          onFileDragLeave={onFileDragLeave}
          onFileDrop={onFileDrop}
          onPickFile={onPickFile}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onStraighten={onStraighten}
          onInterpretClick={onInterpretClick}
          ancho={ancho}
          setAncho={setAncho}
          rapportW={rapportW}
          setRapportW={setRapportW}
          rapportH={rapportH}
          setRapportH={setRapportH}
          direccion={direccion}
          setDireccion={setDireccion}
          setDirectionTouched={setDirectionTouched}
          titulo={titulo}
          setTitulo={setTitulo}
          onTitleCommit={onTitleCommit}
          downloadSvg={downloadSvg}
          onDownloadTilePng={downloadTilePng}
          canDownloadSvg={Boolean(hasSvg || svgText || tileUrl)}
          branch={branch}
          onSave={onSave}
          onDelete={onDelete}
          row={row}
          intent={intent}
          drivePath={drivePath}
          error={error}
          savedNote={savedNote}
          isTile={isTile}
          onGenerateClick={onGenerateClick}
          tileStale={tileStale}
          textileMode
          weave={weave}
          setWeave={setWeave}
          resultSvgUrl={resultSvgUrl}
          seamlessPreviewUrl={seamlessPreviewUrl}
          runHistory={runHistory}
          onSelectHistory={onSelectHistory}
          onDownloadHistorySvg={onDownloadHistorySvg}
          onDownloadHistoryPng={onDownloadHistoryPng}
          lastRunStats={lastRunStats}
        />
      </Stack>

      <SkillConfirmDialog
        open={confirmDelete}
        onClose={() => !deleting && setConfirmDelete(false)}
        onConfirm={onConfirmDelete}
        confirming={deleting}
        title={t("skillAgent.rapport.deleteTitle")}
        description={t("skillAgent.rapport.deleteBody")}
        eyebrow={t("skillAgent.rapport.deleteEyebrow")}
        confirmLabel={t("skillAgent.common.delete")}
        cancelLabel={t("skillAgent.common.cancel")}
        icon={DeleteOutlineIcon}
        tone="error"
        confirmColor="error"
      />
    </Box>
  );
}
